import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const refreshPromiseRef = useRef(null);
  const logoutRef = useRef(null);
  const refreshRef = useRef(null);

  // Stable API instance — created once, interceptors set up once
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: `${API_URL}/api`,
      withCredentials: true,
    });

    // Request interceptor: always reads latest token from localStorage
    instance.interceptors.request.use((config) => {
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    });

    // Response interceptor: auto-refresh on 401
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('/auth/refresh') &&
          !originalRequest.url?.includes('/auth/login')
        ) {
          originalRequest._retry = true;
          try {
            const refreshed = await refreshRef.current?.();
            if (refreshed) {
              originalRequest.headers.Authorization = `Bearer ${refreshed}`;
              return instance(originalRequest);
            }
          } catch {
            // refresh failed
          }
          logoutRef.current?.();
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, []);

  const refreshToken = useCallback(async () => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    const storedRefresh = localStorage.getItem('refresh_token');
    if (!storedRefresh) return null;

    refreshPromiseRef.current = (async () => {
      try {
        const res = await axios.post(`${API_URL}/api/auth/refresh`, {
          refresh_token: storedRefresh,
        }, { withCredentials: true });
        const { token: newToken, refresh_token: newRefresh, user: userData } = res.data;
        localStorage.setItem('token', newToken);
        localStorage.setItem('refresh_token', newRefresh);
        setToken(newToken);
        setUser(userData);
        return newToken;
      } catch {
        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  }, []);

  const doLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
  }, []);

  // Keep refs in sync so interceptors always call latest functions
  refreshRef.current = refreshToken;
  logoutRef.current = doLogout;

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch {
      setUser(null);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (token) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, [token, checkAuth]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, refresh_token: newRefresh, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('refresh_token', newRefresh);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await api.post('/auth/register', userData);
    const { token: newToken, refresh_token: newRefresh, user: newUser } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('refresh_token', newRefresh);
    setToken(newToken);
    setUser(newUser);
    return newUser;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // logout error handled silently
    }
    doLogout();
  };

  const processGoogleSession = async (sessionId) => {
    const response = await api.post('/auth/session', { session_id: sessionId });
    const { session_token, refresh_token: newRefresh, user_id, email, name, picture, role } = response.data;
    localStorage.setItem('token', session_token);
    if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
    setToken(session_token);
    const userData = { user_id, email, name, picture, role };
    setUser(userData);
    return userData;
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    checkAuth,
    processGoogleSession,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isSupplier: user?.role === 'supplier',
    isBuyer: user?.role === 'buyer',
    api,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
